import type { CloudImage } from "$lib/def/seo/cloud-image";

import { Subscription, map, of, switchMap } from "rxjs";
import { createUpdator, watcher, editing$, get, obsIsAdmin } from "../seo";
import { isPossiblyAdminUser } from "./auth";
import { getPlaceHolderImgUrl } from "mock/image";
import { imgSrc } from "../image-details";
import { IMG_HOST } from "../core/env";

export type OnEdit = (currentValue: string, legacyMethod?:boolean) => Promise<{img?:CloudImage, lib:CloudImage[]}> | undefined

export function createImageEditorAction(collection:string, docId:string) {
	const updator = createUpdator(collection, docId);
	const updates$ = watcher(collection, docId);
	const contentPromise = get(collection, docId);
	return (node: HTMLElement, [prop, dimentions, editor]: [string, string, OnEdit, CloudImage[]?]) => {

		let currentValue: string;

		const onActivate = () => {
			node.onclick = (e) => {
				e.preventDefault(); // don't follow potential link
				e.stopPropagation(); // don't go to parent's click event
				// editImage(node as HTMLImageElement, [prop, content]);
				editor?.(currentValue, false)?.then(({img, lib}) => {
					if(!img) return;

					updator(prop, img.hash);
					node.setAttribute('src', getImgUrl(img.hash, dimentions, [img]));
				});
			}
		}

		const onDeactivate = () => {
			node.onclick = null;
		}

		const sub = editing$.subscribe(editing => {
			if (editing) {
				onActivate();
			} else {
				onDeactivate();
			}
		});

		let subFirestore: Subscription;
		if (isPossiblyAdminUser()) { // avoid setting up firebase overhead at all cost
			const $ = obsIsAdmin().pipe(
				// switchMap(isAdmin => isAdmin ? watchPage(content.slug, prop) : of(content[prop])),
				switchMap(isAdmin => isAdmin ? updates$.pipe(map(([,doc]) => doc?.[prop])) : contentPromise.then(item => item?.[1]?.[prop])),
			)
			subFirestore = $.subscribe(value => {
				get<CloudImage>('image', value).then(item => {
					node.setAttribute('src', getImgUrl(item?.[1]?.hash, dimentions, item?.[1] ? [item?.[1]] : []));
				});
			});
		}
		
		// if not set then do so
		contentPromise.then(item => {
			currentValue = item?.[1][prop];
			const currentSrc = node.getAttribute('src'); // check if the value was set (by attribute)
			const targetSrc = imgSrc(currentValue, dimentions);
			if (currentSrc !== targetSrc) node.setAttribute('src', targetSrc); // only set it if it's different
		});

		return {
			destroy () {
				sub.unsubscribe();
				subFirestore?.unsubscribe();
				onDeactivate();
			},
			update ([,,,lib]: [string, string, OnEdit, CloudImage[]?]) {
				if (!lib?.length) return;
				if (!currentValue) return;
				const currentSrc = node.getAttribute('src'); // check if the value was set (by attribute)
				const targetSrc = getImgUrl(currentValue, dimentions, lib);
				if (currentSrc !== targetSrc) node.setAttribute('src', targetSrc); // only set it if it's different
			}
		};
	}
}

export function getImgUrl(value?: string, dim?: string, imageLib?: CloudImage[]) {
	if (!value) return getPlaceHolderImgUrl('[not set]', 120, 60);
	const img = imageLib?.find(img => img.hash === value);
	if (img?.thumbData && (img?.type === 'image/svg+xml' || img?.type === 'svg')) return img.thumbData;
	if (!img?.name) return getPlaceHolderImgUrl('[missing]', 120, 60);
	return `${IMG_HOST}/${dim || '0x0'}/${img.name}`;
}

export function createCloudImageEditorAction(collection:string, docId:string) {
	const updator = createUpdator(collection, docId);
	const updates$ = watcher(collection, docId);
	const contentPromise = get(collection, docId);
	return (node: HTMLElement, [prop, dimentions, editor]: [string, string, OnEdit]) => {

		let currentValue: CloudImage;

		// if not set then do so
		contentPromise.then(item => {
			currentValue = item?.[1][prop];
			const currentSrc = node.getAttribute('src'); // check if the value was set (by attribute)
			const targetSrc = getCimgUrl(currentValue, dimentions);
			if (currentSrc !== targetSrc) node.setAttribute('src', targetSrc); // only set it if it's different
		});


		const onActivate = () => {
			node.onclick = (e) => {
				e.preventDefault(); // don't follow potential link
				e.stopPropagation(); // don't go to parent's click event
				editor?.(currentValue?.hash??'', true)?.then(({img}) => {
					if(!img) return; // TODO: how is an image removed
					updator(prop, img); // with cimg the whole object is the value
					node.setAttribute('src', getCimgUrl(img, dimentions));
				});
			}
		}

		const onDeactivate = () => {
			node.onclick = null;
		}

		const sub = editing$.subscribe(editing => {
			if (editing) {
				onActivate();
			} else {
				onDeactivate();
			}
		});

		let subFirestore: Subscription;
		if (isPossiblyAdminUser()) { // avoid setting up firebase overhead at all cost
			const $ = obsIsAdmin().pipe(
				switchMap(isAdmin => isAdmin ? updates$.pipe(map(([,doc]) => doc?.[prop])) : contentPromise.then(item => item?.[1][prop])),
			)
			subFirestore = $.subscribe((value:CloudImage) => {
				// get<CloudImage>('cimg', value).then(([,img]) => { // no lookup in the image collection needed
				// 	if (!img) return;
				// 	node.setAttribute('src', getCimgUrl(img, dimentions))
				// });
				node.setAttribute('src', getCimgUrl(value, dimentions))
			});
		}
		
		return {
			destroy() {
				sub.unsubscribe();
				subFirestore?.unsubscribe();
				onDeactivate();
			}
		};
	}
}

export function getCimgUrl(img?: CloudImage, dim?:string) {
	if (img?.thumbData && ~img.thumbData.indexOf('image/svg+xml')) return img.thumbData;
	if (!img?.name) return getPlaceHolderImgUrl('[not set]', 120, 60);
	return `${IMG_HOST}/${dim || '0x0'}/${img.name}`;
}
